import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { FaUserShield, FaCheckCircle, FaLock, FaChevronDown, FaChevronRight, FaListUl } from 'react-icons/fa';

const GiveAccess = () => {
  const { token, role, permissions } = useAdminAuth(); //
  const [subordinates, setSubordinates] = useState([]);
  const [availableMenus, setAvailableMenus] = useState<any[]>([]); 
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [perms, setPerms] = useState<any>({}); 
  const [loading, setLoading] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  // 1. My own permissions parse karo (Security check ke liye)
  const myPermissions = typeof permissions === 'string' ? JSON.parse(permissions || "{}") : (permissions || {});

  useEffect(() => {
    // Subordinates fetch karna (Spring Boot - 2027)
    fetch("http://localhost:2027/api/admin/team/my-subordinates", {
      headers: { "Authorization": `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => setSubordinates(data))
    .catch(err => console.error("Error fetching subordinates:", err));

    // Saare sidebar menus fetch karna (Node.js - 2026)
    fetch("http://localhost:2026/api/sidebar/all")
      .then(res => res.json())
      .then(data => setAvailableMenus(Array.isArray(data) ? data : []))
      .catch(err => console.error("Error fetching menus:", err));
  }, [token]);

  const handleUserSelect = (user: any) => {
    setSelectedUser(user);
    try {
      setPerms(JSON.parse(user.permissions || "{}"));
    } catch (e) {
      setPerms({});
    }
  };

  // Logic: Jab sub-item tick ho, toh parent menu automatically tick ho jaye
  const handleSubItemToggle = (parentTitle: string, path: string) => {
    setPerms((prev: any) => {
      const isChecking = !prev[path];
      const newPerms = { ...prev, [path]: isChecking };

      if (isChecking) {
        newPerms[parentTitle] = true; // Auto-tick parent
      }
      return newPerms;
    });
  };

  const saveAccess = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const res = await fetch("http://localhost:2027/api/admin/auth/update-permissions", {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json" 
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          permissions: JSON.stringify(perms)
        })
      }); //

      if (res.ok) {
        alert("Permissions Updated Successfully!");
      } else {
        alert("Failed to save permissions.");
      }
    } catch (e) {
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-8">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#006d5b] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-100">
            <FaUserShield size={20} />
        </div>
        <div>
            <h2 className="text-3xl font-black text-gray-900 tracking-tight uppercase italic">Access Control Center</h2>
            <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Manage hierarchy permissions</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: User Directory */}
        <div className="bg-white rounded-[35px] p-8 shadow-sm border border-gray-50 min-h-[550px]">
          <h3 className="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-6 flex items-center gap-2">
            <FaListUl /> Select Member
          </h3>
          <div className="space-y-3">
            {subordinates.map((sub: any) => (
              <div 
                key={sub.id} 
                onClick={() => handleUserSelect(sub)}
                className={`p-5 rounded-[25px] cursor-pointer transition-all border ${selectedUser?.id === sub.id ? 'bg-[#006d5b]/5 border-[#006d5b]' : 'bg-gray-50 border-transparent hover:bg-gray-100'}`}
              >
                <p className="font-bold text-gray-900">{sub.name}</p>
                <p className="text-[9px] text-[#006d5b] font-black uppercase mt-1 tracking-widest">{sub.role}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Dynamic Module Assignment */}
        <div className="lg:col-span-2 bg-white rounded-[35px] p-10 shadow-sm border border-gray-50 relative">
          {!selectedUser ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-300 opacity-50 py-20">
              <FaLock size={64} className="mb-6" />
              <p className="font-black uppercase text-xs tracking-widest text-center">Select a team member from the left <br/> to configure their sidebar access</p>
            </div>
          ) : (
            <>
              <div className="mb-10 border-b border-gray-50 pb-8 flex justify-between items-end">
                <div>
                    <h3 className="text-2xl font-black text-gray-900">Granting Access to: {selectedUser.name}</h3>
                    <p className="text-gray-400 text-[10px] font-black uppercase mt-1 tracking-widest">Select modules and specific pages to enable</p>
                </div>
              </div>

              <div className="space-y-4">
                {availableMenus.map((menu) => {
                  // SECURITY CHECK: Agar manager hai, toh wahi menu dikhao jo uske paas khud hai
                  const hasAccessToThisGroup = role === 'SUPER_ADMIN' || role === 'ADMIN' || !!myPermissions[menu.title];
                  if (!hasAccessToThisGroup) return null;

                  return (
                    <div key={menu.id} className="border border-gray-100 rounded-[30px] overflow-hidden transition-all">
                      {/* Parent Menu Row */}
                      <div className={`p-6 flex items-center justify-between ${perms[menu.title] ? 'bg-emerald-50/30' : 'bg-white'}`}>
                        <div className="flex items-center gap-4">
                          <input 
                            type="checkbox" 
                            checked={!!perms[menu.title]} 
                            onChange={() => setPerms({...perms, [menu.title]: !perms[menu.title]})}
                            className="w-6 h-6 accent-[#006d5b] cursor-pointer"
                          />
                          <span className={`font-black text-sm uppercase tracking-wider ${perms[menu.title] ? 'text-gray-900' : 'text-gray-400'}`}>{menu.title}</span>
                        </div>
                        <button 
                          onClick={() => setExpandedMenu(expandedMenu === menu.title ? null : menu.title)}
                          className="p-2 hover:bg-gray-100 rounded-full transition-all text-gray-400"
                        >
                          {expandedMenu === menu.title ? <FaChevronDown /> : <FaChevronRight />}
                        </button>
                      </div>

                      {/* Sub-Items (Pages) List */}
                      {expandedMenu === menu.title && (
                        <div className="bg-gray-50/50 p-6 pl-16 space-y-3 border-t border-gray-50 animate-in slide-in-from-top-2">
                          {menu.subItems.map((sub: any) => {
                            // Check if logged-in user has access to this specific sub-page
                            const canIAssignThisPage = role === 'SUPER_ADMIN' || role === 'ADMIN' || !!myPermissions[sub.path];
                            if (!canIAssignThisPage) return null;

                            return (
                                <label key={sub.path} className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 cursor-pointer hover:border-[#006d5b]/30 transition-all shadow-sm">
                                  <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{sub.name}</span>
                                  <input 
                                    type="checkbox" 
                                    checked={!!perms[sub.path]} 
                                    onChange={() => handleSubItemToggle(menu.title, sub.path)}
                                    className="w-5 h-5 accent-[#006d5b]"
                                  />
                                </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button 
                onClick={saveAccess}
                disabled={loading}
                className="w-full mt-12 bg-[#006d5b] hover:bg-[#005a4b] text-white py-6 rounded-[28px] font-black tracking-[0.4em] text-[10px] flex items-center justify-center gap-3 shadow-xl shadow-emerald-100 transition-all active:scale-[0.98] disabled:opacity-50 uppercase"
              >
                {loading ? "COMMITTING CHANGES..." : "AUTHORIZE ACCESS PERMISSIONS"} <FaCheckCircle />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiveAccess;